import { useEffect, useRef, useState } from 'react';
import { usePlaybackStore } from '@/stores';
import type { ScoreTimeline } from '@/types';
import { computeExpectedPitches, computeExpectedHandMap } from '@/utils/expectedNotes';

function setsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/** OTO-23: value-equality for pitch->staff maps (unlike `setsEqual`'s plain
 * membership check, this also compares the staff VALUE per pitch) -- see
 * `useExpectedNotes`'s docstring for why this is checked alongside
 * `setsEqual` rather than relying on the pitch set alone. */
function staffMapsEqual(a: Map<number, 1 | 2>, b: Map<number, 1 | 2>): boolean {
  if (a.size !== b.size) return false;
  for (const [pitch, staff] of a) if (b.get(pitch) !== staff) return false;
  return true;
}

export interface ExpectedNotes {
  pitches: Set<number>;
  /** OTO-23: pitch -> staff for the same expected notes, so PianoKeyboard
   * can glow by hand. See `computeExpectedHandMap` for the unison tie-break. */
  staffByPitch: Map<number, 1 | 2>;
}

/**
 * OTO-21: computes the expected-pitch set (+ OTO-23: per-pitch staff map)
 * for `PianoKeyboard` highlighting via a transient (non-rendering)
 * `usePlaybackStore.subscribe`, calling `setState` only when either the
 * pitch set OR the staff mapping actually changes (chord boundaries)
 * rather than on every currentTime tick. Both are checked -- not just the
 * pitch set -- because a pitch's staff can change between two chord
 * windows (e.g. staff-2 plays 60, then staff-1 plays 60) while the pitch
 * set itself stays identical, which `setsEqual` alone would miss.
 * Extracted from `PracticeView` so this no longer forces a 60fps React
 * re-render of the whole practice view.
 */
export function useExpectedNotes(timeline: ScoreTimeline | null | undefined): ExpectedNotes {
  const timelineRef = useRef(timeline);
  timelineRef.current = timeline;

  const [expected, setExpected] = useState<ExpectedNotes>(() => ({
    pitches: new Set(),
    staffByPitch: new Map(),
  }));
  const expectedRef = useRef(expected);

  useEffect(() => {
    const update = () => {
      const s = usePlaybackStore.getState();
      const nextPitches = computeExpectedPitches(
        timelineRef.current,
        s.position.currentTime,
        s.config.hand
      );
      const nextStaffByPitch = computeExpectedHandMap(
        timelineRef.current,
        s.position.currentTime,
        s.config.hand
      );
      if (
        !setsEqual(expectedRef.current.pitches, nextPitches) ||
        !staffMapsEqual(expectedRef.current.staffByPitch, nextStaffByPitch)
      ) {
        const next = { pitches: nextPitches, staffByPitch: nextStaffByPitch };
        expectedRef.current = next;
        setExpected(next);
      }
    };

    update();
    return usePlaybackStore.subscribe(update);
  }, [timeline]);

  return expected;
}
